import { useState } from 'react';
import axios from 'axios';
import language from '../../data/language';
import notify, { apiFieldErrors, currentLanguage } from '../../common/ui/notify';

const EMPTY_FORM = {name: "", phone: "", email: "", message: ""};

/**
 * "Interested in this car?" — the public form that turns a visitor into a lead
 * attached to the car being viewed.
 */
const CarEnquiryForm = ({carId, carLabel}) => {
    const lang = currentLanguage();
    const strings = language[lang].details;

    const [form, setForm] = useState(EMPTY_FORM);
    const [fieldErrors, setFieldErrors] = useState({});
    const [sending, setSending] = useState(false);

    const update = (field) => (event) => {
        const {value} = event.target;
        setForm((prev) => ({...prev, [field]: value}));
        setFieldErrors((prev) => (prev[field] ? {...prev, [field]: undefined} : prev));
    };

    const submit = async (event) => {
        event.preventDefault();
        if (sending) return; // the guard against a double submit

        setSending(true);
        setFieldErrors({});

        try {
            await axios.post('/api/leads', {
                source: 'car_enquiry',
                car_id: carId,
                name: form.name,
                phone: form.phone,
                email: form.email,
                message: form.message,
                manufacturer: carLabel
            });
            notify.success(strings.request_sent);
            setForm(EMPTY_FORM); // cleared only after the server confirmed
        } catch (error) {
            setFieldErrors(apiFieldErrors(error));
            notify.apiError(error);
        } finally {
            setSending(false);
        }
    };

    const field = (name, label, {type = "text", required = false, textarea = false} = {}) => (
        <div className="cx-field">
            <label className="cx-label" htmlFor={`enquiry-${name}`}>
                {label}{required && <span className="cx-required" aria-hidden="true">*</span>}
            </label>
            {textarea ? (
                <textarea
                    id={`enquiry-${name}`}
                    className={`cx-textarea ${fieldErrors[name] ? "cx-textarea-invalid" : ""}`}
                    value={form[name]}
                    required={required}
                    rows={3}
                    aria-invalid={Boolean(fieldErrors[name])}
                    onChange={update(name)}
                />
            ) : (
                <input
                    id={`enquiry-${name}`}
                    className={`cx-input ${fieldErrors[name] ? "cx-input-invalid" : ""}`}
                    type={type}
                    value={form[name]}
                    required={required}
                    aria-invalid={Boolean(fieldErrors[name])}
                    onChange={update(name)}
                />
            )}
            {fieldErrors[name] && <p className="cx-error-text">{fieldErrors[name]}</p>}
        </div>
    );

    return (
        <form className="car-enquiry" onSubmit={submit} noValidate={false}>
            <h3 className="car-enquiry-title">{strings.request_info}</h3>
            <p className="car-enquiry-text">{strings.request_info_text}</p>

            {field('name', strings.your_name, {required: true})}
            {field('phone', strings.your_phone, {type: "tel", required: true})}
            {field('email', strings.your_email, {type: "email"})}
            {field('message', strings.your_message, {textarea: true})}

            <button type="submit" className="cx-btn cx-btn-primary cx-btn-block" disabled={sending}>
                {sending
                    ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> {strings.sending}</>
                    : strings.send_request}
            </button>
        </form>
    );
};

export default CarEnquiryForm;
